import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { Gate } from '../../../global/contexts/auth/auth';
import Modal from '../../../common/components/modal/modal';
import { usePinStandard, useStandardCatalog, useStandardVersion, useStandardVersions } from '../contexts/standards';
import '../styles/standards.css';

const date = value => value ? new Date(value).toLocaleString('es-CO') : 'sin fecha registrada';

export default function RepositoryStandards({ repository }) {
    return <Gate domain="standards:read" fallback={<p>Necesitas permiso para consultar estándares.</p>}>
        <StandardPanel repository={repository} />
    </Gate>;
}

function StandardPanel({ repository }) {
    const catalog = useStandardCatalog();
    const current = useStandardVersion(repository.standard_version_id);
    const [standardId, setStandardId] = useState('');
    const [versionId, setVersionId] = useState('');
    const [confirmation, setConfirmation] = useState(null);
    const [failure, setFailure] = useState('');
    const [uncertain, setUncertain] = useState(false);
    const submitting = useRef(false);
    const versions = useStandardVersions(standardId);
    const pin = usePinStandard(repository.id);
    const standards = catalog.data ?? [];
    const standard = standards.find(item => String(item.id) === standardId);
    const published = (versions.data ?? []).filter(item => item.version_state === 'published')
        .sort((a, b) => (Date.parse(b.published_at) || 0) - (Date.parse(a.published_at) || 0) || b.id - a.id);
    const selected = published.find(item => String(item.id) === versionId);
    const assigned = standards.find(item => item.id === current.data?.standard_id);

    const close = () => { if (!submitting.current) setConfirmation(null); };
    async function save() {
        if (submitting.current || uncertain || !confirmation) return;
        submitting.current = true;
        setFailure('');
        try {
            await pin.mutateAsync({ standard_version_id: confirmation.version.id,
                expected_standard_version_id: confirmation.previousId });
            setConfirmation(null);
            toast.success('Estándar asociado y verificado');
        } catch (error) {
            setFailure(error.message || 'No se pudo confirmar la asociación.');
            // A lost response may hide a committed write. Read again before another attempt.
            setUncertain(!error.status || error.status >= 500 || error.status === 409);
        } finally { submitting.current = false; }
    }

    return <section className="repository-standards" aria-label="Estándar del repositorio">
        <h3>Estándar del repositorio</h3>
        <p>Fija una versión publicada del catálogo. Publicar versiones nuevas no cambia esta elección.</p>
        <div className="repository-standards__current" aria-live="polite">
            {!repository.standard_version_id ? <strong>Sin estándar asociado</strong> : <>
                <strong>Versión asociada · ID {repository.standard_version_id}</strong>
                {current.isPending && <p>Cargando versión...</p>}
                {current.error && <p role="alert">No se pudo leer la versión: {current.error.message}</p>}
                {current.data && <p>{assigned?.name ?? `Estándar #${current.data.standard_id}`} · {assigned?.track}
                    {' · '}{current.data.number} · {current.data.version_state} · {date(current.data.published_at)}</p>}
            </>}
        </div>
        {catalog.isPending && <p>Cargando catálogo...</p>}
        {catalog.error && <p role="alert">No se pudo cargar el catálogo: {catalog.error.message}
            {' '}<button type="button" onClick={() => catalog.refetch()}>Reintentar</button></p>}
        {catalog.isSuccess && !standards.length && <p>No hay estándares disponibles en el catálogo.{' '}
            <Gate domain="standards:write"><Link to="/admin/standards/new">Crear el primer estándar</Link></Gate>
        </p>}
        <Gate domain="repositories:write">
            <label>Estándar y pista
                <select value={standardId} disabled={catalog.isPending || !!catalog.error || pin.isPending}
                    onChange={e => { setStandardId(e.target.value); setVersionId(''); }}>
                    <option value="">Selecciona un estándar</option>
                    {standards.map(item => <option key={item.id} value={item.id}>
                        {item.name} · {item.track} · {item.code} · ID {item.id}
                    </option>)}
                </select>
            </label>
            {standard?.description && <p>{standard.description}</p>}
            {standardId && <>
                {versions.isPending && <p>Cargando versiones...</p>}
                {versions.error && <p role="alert">No se pudieron leer las versiones: {versions.error.message}</p>}
                {versions.isSuccess && !published.length && <p>Este estándar aún no tiene versiones publicadas.</p>}
                <label>Versión publicada
                    <select value={versionId} onChange={e => setVersionId(e.target.value)}
                        disabled={versions.isPending || !!versions.error || pin.isPending}>
                        <option value="">Selecciona una versión</option>
                        {published.map((item, index) => <option key={item.id} value={item.id}>
                            {item.number} · ID {item.id} · {date(item.published_at)}{index === 0 ? ' · Publicación más reciente' : ''}
                        </option>)}
                    </select>
                </label>
            </>}
            <button type="button" className="client-form__submit"
                disabled={!selected || selected.id === repository.standard_version_id || pin.isPending}
                onClick={() => {
                    setFailure(''); setUncertain(false);
                    setConfirmation({ standard, version: selected, previousId: repository.standard_version_id ?? null });
                }}>Asociar estándar</button>
        </Gate>
        <p>Esta operación no desbloquea ni aprueba tareas. Tras verificar la asociación, puedes desbloquear la tarea pendiente desde su detalle.</p>
        <Modal open={!!confirmation} onClose={close} busy={pin.isPending} title="Confirmar estándar"
            actions={<>
                <button type="button" onClick={close} disabled={pin.isPending}>Cancelar</button>
                <button type="button" className="client-form__submit" onClick={save}
                    disabled={pin.isPending || uncertain}>{pin.isPending ? 'Verificando...' : 'Confirmar asociación'}</button>
            </>}>
            {confirmation && <>
                <p><strong>{repository.name}</strong> usará {confirmation.standard.name} ({confirmation.standard.track}),
                    versión <strong>{confirmation.version.number}</strong> · ID {confirmation.version.id}.</p>
                <p>{confirmation.previousId ? `Reemplaza la versión con ID ${confirmation.previousId}.` : 'Actualmente no tiene un estándar asociado.'}</p>
                <p>Publicada: {date(confirmation.version.published_at)}.</p>
            </>}
            {failure && <p role="alert">{failure}</p>}
            {uncertain && <p>No pudimos confirmar el resultado. Cierra y recarga el repositorio antes de volver a intentarlo.</p>}
        </Modal>
    </section>;
}
